import { useState, useEffect, useCallback } from 'react'
import { apiClient } from '../api/client'
import type { ParentCommentListResponse } from '../api/types'

type UseParentCommentsResult = {
  data: ParentCommentListResponse | null
  loading: boolean
  error: string | null
  loadMore: () => void
  refetch: () => void
}

/**
 * 保護者連絡帳コメント一覧を取得するフック（最新順・カーソルページネーション）
 *
 * カーソルありで追加読み込み（既存データに追記）、なしで先頭から取得。
 */
export function useParentComments(): UseParentCommentsResult {
  const [data, setData] = useState<ParentCommentListResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const fetchComments = useCallback(async (cursor?: string) => {
    setLoading(true)
    setError(null)

    try {
      const result = await apiClient.getParentComments({ cursor, limit: 20 })

      setData((prev) =>
        cursor && prev
          ? // ページネーション: 既存データに追加
            { comments: [...prev.comments, ...result.comments], nextCursor: result.nextCursor }
          : // 初回読み込み / 再取得: データを置き換え
            result
      )
    } catch (err) {
      console.error('Failed to fetch parent comments:', err)
      setError(err instanceof Error ? err.message : 'コメントの取得に失敗しました')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchComments()
  }, [fetchComments])

  const loadMore = useCallback(() => {
    if (data?.nextCursor && !loading) {
      fetchComments(data.nextCursor)
    }
  }, [data?.nextCursor, loading, fetchComments])

  return {
    data,
    loading,
    error,
    loadMore,
    refetch: () => fetchComments(),
  }
}
